// THE INFECTED — client simulation + rendering (docs/INFECTED.md, Z1–Z116).
//
// Architecture note (read this before changing anything):
//
//   The design contract makes the infected SERVER-AUTHORITATIVE (server/
//   zombies.mjs, 5 Hz, snapshot broadcast, client mirrors). That server module
//   does not exist yet, so this file ships the same brain CLIENT-SIDE:
//
//     • `InfectedSim` is deliberately Babylon-free and talks to the world only
//       through the `env` adapter it is handed (players / biome / walls /
//       ground). Lifting it into `server/zombies.mjs` is a file move plus a
//       Node-side `env`; nothing in it imports the browser.
//     • `Infected` (the renderer) NEVER reads the sim directly. It consumes
//       `snapshot()` rows — [id, x, z, yaw, state, flags, ev] — exactly the
//       shape the server is specced to broadcast (Z79), and interpolates them
//       the way `Animals._updateRemote` mirrors the wolves.
//     • `applyServerInfected(rows)` is the mirror hook: the first call flips
//       the client into mirror mode, drops the local sim and renders the
//       server's truth instead. `onNet` then reports damage as {k:'inf-dmg'}.
//
//   So: same rows, same renderer, one switch. When server/zombies.mjs lands,
//   wire `onInfected` in net/client.js to `zombies.applyServerInfected` and
//   this file needs no other change.
//
// Rendering (Z75–Z82): three tiers per client — up to 8 skinned rigs cloned
// from the already-loaded character.glb inside 55 m, up to 44 thin-instanced
// shambler proxies out to 160 m, nothing beyond. Rigs are pooled, never
// disposed while streaming.
//
// They must not read as the player rig in a different colour, so the near tier
// gets: the zombie clip set (hunched shamble / broken run / claw attacks), a
// per-entity BROKEN POSE applied after the animator every frame (spine hunch,
// head tilt, dropped shoulder, dangling arm), gaunt non-uniform scale, a
// rolling lurch on the root, and a sickly desaturated material set.
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Vector3, Quaternion, Matrix } from '@babylonjs/core/Maths/math';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Animator } from '../player/animator';
import { buildGarments } from '../player/garments';
import { noiseRadius, visibility, sanitizePing } from '../game/noise';
import { meleeSwing } from './melee-arc.mjs';

// The brain now lives in ./infected-sim.mjs — ONE copy of the AI, imported by
// this renderer and by server/zombies.mjs (docs/INFECTED.md Z1/Z5). Everything
// it used to export from here is re-exported, so no other module changes.
import { INF, ST, ST_NAMES, EV, InfectedSim, hash2, angWrap } from './infected-sim.mjs';

export { INF, ST, ST_NAMES, EV, InfectedSim };

// ===========================================================================
// THE CLIENT — mirrors snapshot rows, owns meshes, poses, corpses, sfx and the
// melee/hitscan surface. It never simulates AI when a server owns the truth.
// ===========================================================================
const NEAR_R = 55;         // full skinned rig inside this (Z75)
const NEAR_KEEP = 64;      // hysteresis so a rig does not pop on the boundary
const NEAR_MAX = 8;
const MID_R = 160;
const MID_MAX = 44;
const HITBOX_MAX = 10;     // pickable capsules for the gun hitscan
const HITBOX_R = 110;

// THREE hit zones, measured in metres above the entity's feet.
//
// 'legs' is new and it is not cosmetic: the whole leg-crippling chain already
// exists and has never had a producer, because every hit ever reported 'head'
// or 'body'. The consumers, all verified present:
//   infected-sim.mjs:28     INF.limbMul 0.7, INF.legCripple 60
//   infected-sim.mjs:593     the zone === 'legs' damage multiplier
//   infected-sim.mjs:598-605 e.legs accumulator -> e.crawler = true, ST.crawl
//   infected-sim.mjs:414/481 crawler speed and INF.atk.crawlRange
//   zombies.js CLIPS 'z-crawl' + _poseFor -> a.setBase('z-crawl')
//   server/zombies.mjs:434   the server-side zone validator, which already
//                            accepts 'legs' and has only ever received 'body'
// These are the same two cuts main.js hands the query service as headY0 /
// legsY1 for its body records, so a legacy mesh pick and a query ray classify
// the same shot identically.
// Exported so there is exactly ONE definition of the cuts: main.js hands these
// same two numbers to query.mjs as the body records' legsY1 / headY0, and the
// pickable capsules below are sized from legsTop.
export const INF_ZONE_Y = {
  legsTop: 0.9,    // at or below this -> 'legs'
  headBot: 1.45,   // at or above this -> 'head'; between the two -> 'body'
  feet: 0.1,       // bottom of the leg capsule (ankles, not the sole)
  crown: 1.75,     // top of the torso capsule
};

// v1 clip set (Z78) + the limp/broken variants that sell a crowd
const CLIPS = [
  ['z-idle', 'zombie-idle', true], ['z-idle-sway', 'zombie-idle-sway', true],
  ['z-walk', 'zombie-walk', true], ['z-walk-shamble', 'zombie-walk-shamble', true],
  ['z-walk-lurch', 'zombie-walk-lurch', true], ['z-walk-limp', 'zombie-walk-limp', true],
  ['z-run', 'zombie-run', true], ['z-run-fast', 'zombie-run-fast', true],
  ['z-run-broken', 'zombie-run-broken', true],
  ['z-crawl', 'zombie-crawl', true],
  ['z-scream', 'zombie-scream', false],
  ['z-attack', 'zombie-attack-claw-b', false], ['z-attack-b', 'zombie-attack-swipe', false],
  ['z-stagger', 'zombie-stagger-front', false], ['z-flinch', 'zombie-flinch-head', false],
  ['z-death', 'zombie-death', false], ['z-death-b', 'zombie-death-fwd', false],
  ['z-getup', 'zombie-getup', false],
];

const IDLES = ['z-idle', 'z-idle-sway'];
const WALKS = ['z-walk', 'z-walk-shamble', 'z-walk-lurch', 'z-walk-limp'];
const RUNS = ['z-run', 'z-run-fast', 'z-run-broken'];
const ATTACKS = ['z-attack', 'z-attack-b'];
const DEATHS = ['z-death', 'z-death-b'];

// voice roles (Z85): [role, earshot, volume]
const VOICE = {
  idle: ['creature.zombie.idle', 35, 0.30],
  moan: ['creature.zombie.moan', 45, 0.38],
  alert: ['creature.zombie.alert', 140, 0.75],
  chase: ['creature.zombie.chase', 60, 0.55],
  attack: ['creature.zombie.attack', 20, 0.60],
  hit: ['creature.zombie.hit', 25, 0.55],
  death: ['creature.zombie.death', 50, 0.60],
  footfall: ['creature.zombie.footfall', 14, 0.25],
};

const UPPER_RE = /Spine|Neck|Head|Shoulder|Arm|Hand/;

export class Infected {
  // opts: { terrain, buildings, veg, sfx, effects, camera, engine, shadows,
  //         clips, boneMap, player, input, world, mood }
  constructor(scene, opts) {
    this.scene = scene;
    this.o = opts;
    this.views = new Map();   // id -> view
    this.pool = [];           // skinned rig pool (Z82)
    this.sim = null;
    this.serverOwned = false;
    this.rows = [];
    this.master = null;       // retargeted zombie clips (never played directly)
    this._acc = 0;
    this._camYaw = 0;
    this._voiceT = 0;         // horde limiter (Z88)
    this._voices = [];
    this._warmed = false;
    this._ready = false;
    this._makingRig = false;
  }

  async init() {
    const { veg, buildings, terrain } = this.o;
    const towns = await (await fetch('assets/map10k/towns.json')).json();
    // The sim only ever sees these four functions — swapping them for a Node
    // implementation is all server/zombies.mjs has to do.
    //
    // …unless the server already spoke. `init()` is async and NOT awaited by
    // main.js, so the welcome (which carries `world.infected` and calls
    // applyServerInfected) routinely lands while the towns.json fetch above is
    // still in flight. Building the sim here anyway would resurrect the local
    // brain that applyServerInfected just dropped "for good" — inert, because
    // update() gates on serverOwned, but a whole town plan built for nothing
    // and a debug() that lies about who owns the horde. Mirror mode wins.
    if (!this.serverOwned) {
      this.sim = new InfectedSim({
        towns,
        biomeAt: (x, z) => veg.biomeAt(x, z),
        blocked: (x, z) => {
          const gy = terrain.getHeight(x, z);
          return buildings.blockedBand(x, z, gy + 0.35, gy + 1.5);
        },
        players: () => this._players(),
        hit: (ev) => this.onHit?.(ev),
      });
    }
    // every loud one-shot in the game flows through world.noise (Z27–Z28);
    // once the server sim lands this listener moves to mp-server's relay
    this.o.world?.noise?.listen?.((ev) => {
      if (!this.serverOwned) this.sim?.ping(ev.x, ev.z, ev.r);
    });
    this._buildProxy();
    this._buildBlood();
    this.scene.onBeforeRenderObservable.add(() => {
      const dt = Math.min(this.scene.getEngine().getDeltaTime() / 1000, 0.05);
      this.update(dt, this.o.camera?.yaw ?? 0);
    });
    // the broken-pose overlay has to run AFTER the animator has written the
    // bones, same slot the player's own overlays use
    this.scene.onAfterAnimationsObservable.add(() => this._poseOverlay());
    // NB: the zombie clip set is NOT loaded here. Boot already pays for 100+
    // player clips; these 18 load the first time an infected comes within
    // 150 m (Z77), by which time it is still a mid-tier proxy.
    this._ready = true;
    return this;
  }

  // ------------------------------------------------------- player adapter
  _players() {
    const { player, input, world } = this.o;
    if (!player) return [];
    const p = player.root.position;
    // main.js owns the emitter (world.noise); fall back to the pure functions
    // so ?gym=1 and the node harness need no NoiseSystem
    let sense = world?.noiseState?.();
    if (!sense) {
      const s = {
        x: p.x, z: p.z, stance: player.stance || 'stand',
        speed: player.velocity ? Math.hypot(player.velocity.x, player.velocity.z) : 0,
        sneak: !!input?.sneak, torch: !!player.torch,
      };
      sense = { n: noiseRadius(s, world), v: visibility(s, this.o.mood?.()) };
    }
    const out = [{
      id: 'local', x: p.x, z: p.z, yaw: player.heading, dead: !!player.dead,
      n: sense.n, v: sense.v,
    }];
    // other survivors are targets too. Their own client folds stance, speed,
    // surface and sneak into `n`/`v` and ships them on the 10 Hz packet (Z22),
    // so a crouching neighbour really is quieter here; a client too old to send
    // them is treated as a walking player.
    for (const r of this.o.remotes?.remotes?.values?.() || []) {
      if (!r.root?.isEnabled() || r.dead) continue;
      const st = r.lastState;
      out.push({
        id: r.id, x: r.root.position.x, z: r.root.position.z, yaw: r.yaw,
        dead: false,
        n: Number.isFinite(st?.n) ? Math.max(0, Math.min(45, st.n)) : 12,
        v: Number.isFinite(st?.v) ? Math.max(0.05, Math.min(2, st.v)) : 0.9,
        remote: true,
      });
    }
    return out;
  }

  // ------------------------------------------------------------ server hook
  // The first call flips this client into mirror mode for good: the local sim
  // is dropped and the server's rows drive everything (Z4).
  applyServerInfected(rows) {
    if (!this.serverOwned) {
      this.serverOwned = true;
      this.sim = null;
    }
    this.rows = rows || [];
    this._applyRows(this.rows);
  }

  // ---------------------------------------------------------------- assets
  async _container() {
    if (this._containerP) return this._containerP;
    // the same file RemotePlayers loads — zero extra download (Z76)
    this._containerP = LoadAssetContainerAsync('assets/models/character.glb', this.scene);
    return this._containerP;
  }

  // Split-retarget onto the local player's node map. These master groups are
  // NEVER started; each rig clones the ones it actually plays (the remotes.js
  // lazy-Proxy pattern, Z77). Loaded lazily, after the world is up.
  async _loadClips() {
    const map = this.o.boneMap;
    if (!map) return {};
    const out = {};
    const queue = [...CLIPS];
    const worker = async () => {
      for (let d = queue.shift(); d; d = queue.shift()) {
        const [name, file, loop] = d;
        try {
          const c = await LoadAssetContainerAsync(`assets/anims/mixamo/${file}.glb`, this.scene);
          const src = c.animationGroups[0];
          const L = new AnimationGroup(name + '#L', this.scene);
          const U = new AnimationGroup(name + '#U', this.scene);
          let fps = 30, maxFrame = 0;
          for (const ta of src.targetedAnimations) {
            const tn = ta.target?.name;
            const target = map.get(tn);
            if (!target) continue;
            let anim = ta.animation;
            if (tn === 'mixamorig:Hips' && anim.targetProperty === 'position') {
              anim = anim.clone();
              for (const k of anim.getKeys()) { k.value.x = 0; k.value.z = 0; }
            }
            fps = anim.framePerSecond || fps;
            const keys = anim.getKeys();
            if (keys.length) maxFrame = Math.max(maxFrame, keys[keys.length - 1].frame);
            (UPPER_RE.test(tn) ? U : L).addTargetedAnimation(anim, target);
          }
          out[name] = { L, U, dur: maxFrame / fps, loop };
          c.dispose();
        } catch (err) {
          console.warn('infected clip failed:', file, err.message);
        }
      }
    };
    await Promise.all([worker(), worker(), worker(), worker()]);
    this.master = out;
    return out;
  }

  // one merged low-poly shambler, thin-instanced for the mid tier (Z75)
  _buildProxy() {
    const S = this.scene;
    const mat = new StandardMaterial('infProxyMat', S);
    mat.diffuseColor = new Color3(0.24, 0.25, 0.21);
    mat.emissiveColor = new Color3(0.05, 0.055, 0.045); // never a flat black cut-out
    mat.specularColor = Color3.Black();
    const parts = [];
    const add = (m, x, y, z, rx = 0, rz = 0) => {
      m.position.set(x, y, z); m.rotation.x = rx; m.rotation.z = rz;
      m.material = mat; parts.push(m); return m;
    };
    // hunched torso, head thrust forward, arms hanging out in front: the
    // silhouette must read as "not a person" at 100 m
    add(MeshBuilder.CreateBox('p_torso', { width: 0.42, height: 0.72, depth: 0.3 }, S), 0, 1.16, 0.06, 0.38);
    add(MeshBuilder.CreateBox('p_hip', { width: 0.36, height: 0.3, depth: 0.26 }, S), 0, 0.86, -0.04);
    add(MeshBuilder.CreateSphere('p_head', { diameter: 0.25, segments: 6 }, S), 0, 1.53, 0.28, 0.3);
    for (const s of [-1, 1]) {
      add(MeshBuilder.CreateBox('p_arm', { width: 0.11, height: 0.62, depth: 0.11 }, S),
        s * 0.26, 1.16, 0.26, 1.15 + s * 0.25, s * 0.12);
      add(MeshBuilder.CreateBox('p_leg', { width: 0.14, height: 0.8, depth: 0.14 }, S),
        s * 0.12, 0.4, s * 0.05, s * 0.06);
    }
    const merged = Mesh.MergeMeshes(parts, true, true, undefined, false, false);
    merged.name = 'infProxy';
    merged.isPickable = false;
    merged.alwaysSelectAsActiveMesh = true;
    merged.receiveShadows = false;
    this.proxy = merged;
    this._mtx = new Float32Array(MID_MAX * 16);
    merged.thinInstanceSetBuffer('matrix', this._mtx, 16, false);
    merged.thinInstanceCount = 0;
    this._tmpQ = new Quaternion();
    this._tmpM = new Matrix();
    this._tmpS = new Vector3(1, 1, 1);
    this._tmpP = new Vector3();
  }

  // blood spray on impact — effects.js owns the dust/spark pools and we may
  // not edit it, so this is our own two-emitter pool over its dust texture
  _buildBlood() {
    const tex = this.o.effects?.dustTex;
    this.blood = [];
    for (let i = 0; i < 2; i++) {
      const ps = new ParticleSystem('infBlood' + i, 22, this.scene);
      if (tex) ps.particleTexture = tex;
      ps.emitter = new Vector3(0, -200, 0);
      ps.minEmitBox = new Vector3(-0.05, -0.05, -0.05);
      ps.maxEmitBox = new Vector3(0.05, 0.05, 0.05);
      ps.color1 = new Color4(0.42, 0.03, 0.03, 0.95);
      ps.color2 = new Color4(0.24, 0.02, 0.02, 0.8);
      ps.colorDead = new Color4(0.12, 0.01, 0.01, 0);
      ps.minSize = 0.04; ps.maxSize = 0.14;
      ps.minLifeTime = 0.18; ps.maxLifeTime = 0.45;
      ps.emitRate = 0; ps.manualEmitCount = 0;
      ps.gravity = new Vector3(0, -9, 0);
      ps.minEmitPower = 1.2; ps.maxEmitPower = 3.4;
      ps.updateSpeed = 0.016;
      ps.start();
      this.blood.push(ps);
    }
    this._blI = 0;
  }

  _spray(x, y, z, dirX, dirZ) {
    if (!this.blood?.length) return;
    const ps = this.blood[this._blI++ % this.blood.length];
    ps.emitter.set(x, y, z);
    ps.direction1 = new Vector3(dirX * 2 - 0.6, 0.4, dirZ * 2 - 0.6);
    ps.direction2 = new Vector3(dirX * 2 + 0.6, 1.6, dirZ * 2 + 0.6);
    ps.manualEmitCount = 10 + (Math.random() * 8 | 0);
  }

  // ------------------------------------------------------------------- rigs
  async _makeRig() {
    if (this._makingRig || this.pool.length >= NEAR_MAX) return;
    this._makingRig = true;
    try {
      this._clipsP ||= this._loadClips();
      const master = this.master || await this._clipsP;
      if (!master || !Object.keys(master).length) return;
      const c = await this._container();
      const inst = c.instantiateModelsToScene((n) => n, false);
      const root = new TransformNode('infRig' + this.pool.length, this.scene);
      root.rotationQuaternion = Quaternion.Identity();
      for (const rn of inst.rootNodes) rn.parent = root;
      inst.animationGroups?.forEach((g) => g.stop());

      const map = new Map();
      const meshes = [];
      for (const rn of inst.rootNodes) {
        for (const n of [rn, ...rn.getDescendants()]) {
          if (n.name.startsWith('mixamorig:') || n.getClassName?.() === 'Mesh') map.set(n.name, n);
          if (n.getClassName?.() === 'Mesh') meshes.push(n);
        }
      }
      // rags: reuse the player's garment builder, then throw the gloves away —
      // bare filthy hands read as claws and break the "player in another
      // colour" silhouette immediately
      let garments = [];
      try { garments = buildGarments(this.scene, meshes) || []; } catch { garments = []; }
      for (let i = garments.length - 1; i >= 0; i--) {
        if (/glove/i.test(garments[i].name)) { garments[i].dispose(); garments.splice(i, 1); }
      }
      const skin = [...meshes, ...garments];
      const variant = this.pool.length % 3;
      const mats = [];
      for (const m of skin) {
        m.isPickable = false;
        m.receiveShadows = false;
        m.material = this._infMaterial(m.material, variant, this.pool.length);
        if (m.material && !mats.includes(m.material)) mats.push(m.material);
      }
      if (this.pool.length < 3) for (const m of skin) this.o.shadows?.addShadowCaster(m);

      // lazy clip cache: clone only the clips this rig actually plays (Z77)
      const cache = {};
      const id = this.pool.length;
      const clips = new Proxy(cache, {
        get(t, key) {
          if (typeof key !== 'string') return t[key];
          if (t[key]) return t[key];
          const src = master[key];
          if (!src) return undefined;
          const conv = (old) => map.get(old?.name) || old;
          t[key] = {
            L: src.L.clone(`${key}#L@z${id}`, conv),
            U: src.U.clone(`${key}#U@z${id}`, conv),
            dur: src.dur, loop: src.loop,
          };
          return t[key];
        },
        has(t, key) { return key in t || key in master; },
      });
      const animator = new Animator(clips);

      const rig = {
        root, map, animator, clips, meshes: skin, mats,
        bones: {
          spine: map.get('mixamorig:Spine'), spine1: map.get('mixamorig:Spine1'),
          spine2: map.get('mixamorig:Spine2'), neck: map.get('mixamorig:Neck'),
          headB: map.get('mixamorig:Head'),
          lShoulder: map.get('mixamorig:LeftShoulder'), rShoulder: map.get('mixamorig:RightShoulder'),
          lArm: map.get('mixamorig:LeftArm'), rArm: map.get('mixamorig:RightArm'),
        },
        view: null,
      };
      root.setEnabled(false);
      this.pool.push(rig);
    } catch (err) {
      console.warn('infected rig failed:', err.message);
    } finally {
      this._makingRig = false;
    }
  }

  // sickly, desaturated, darker than any survivor — never touches the player's
  // own materials (they are shared with the container, so we clone first)
  // One material set PER RIG (not per variant): the hit flash writes emissive,
  // and a shared material would light up the whole horde when one is shot.
  _infMaterial(src, variant, rigId) {
    if (!src) return src;
    this._mats ||= new Map();
    const key = src.name + '#' + rigId;
    if (this._mats.has(key)) return this._mats.get(key);
    const m = src.clone('inf_' + key);
    const TINT = [
      new Color3(0.55, 0.60, 0.52), // grey-green
      new Color3(0.62, 0.55, 0.50), // ashen
      new Color3(0.45, 0.46, 0.48), // corpse-blue
    ][variant] || new Color3(0.55, 0.6, 0.52);
    if (m.albedoColor) {
      m.albedoColor = m.albedoColor.multiply(TINT).scale(0.85);
      m.metallic = 0;
      m.roughness = 0.95;
      m.specularIntensity = 0.08; // dead skin does not shine
      // the survivor gets appearance.js's fill light; the infected must not
      // (they should be the darker thing in the frame) — but without SOME lift
      // they crush to a flat black cut-out under an overcast sky
      m.emissiveColor = TINT.scale(0.055);
    } else if (m.diffuseColor) {
      m.diffuseColor = m.diffuseColor.multiply(TINT).scale(0.85);
      m.emissiveColor = m.diffuseColor.scale(0.18);
      m.specularColor = Color3.Black();
    }
    m._infBase = (m.emissiveColor || Color3.Black()).clone(); // hit-flash base
    this._mats.set(key, m);
    return m;
  }

  // hit flash: lift the entity's own emissive toward blood-red and decay it
  _flashRig(rig, amount) {
    if (!rig.mats) return;
    for (const m of rig.mats) {
      const b = m._infBase;
      if (!b) continue;
      if (amount <= 0) { m.emissiveColor.copyFrom(b); continue; }
      m.emissiveColor.set(b.r + amount * 0.55, b.g + amount * 0.06, b.b + amount * 0.05);
    }
  }

  // ------------------------------------------------------------------ views
  _makeView(id, x, z, yaw) {
    const h = hash2(id, 7, 61), h2n = hash2(id, 9, 62), h3 = hash2(id, 11, 63);
    return {
      id, x, z, y: 0, yaw, netX: x, netZ: z, netYaw: yaw,
      state: -1, flags: 0, crawler: false, rig: null, dead: false,
      // per-entity variation: gait, tempo, voice pitch, silhouette
      idleClip: IDLES[(h * IDLES.length) | 0] || IDLES[0],
      walkClip: WALKS[(h2n * WALKS.length) | 0] || WALKS[0],
      runClip: RUNS[(h3 * RUNS.length) | 0] || RUNS[0],
      atkClip: ATTACKS[(h * ATTACKS.length) | 0] || ATTACKS[0],
      deathClip: DEATHS[(h2n * DEATHS.length) | 0] || DEATHS[0],
      rate: 0.86 + h * 0.3,
      pitch: 0.85 + h2n * 0.15,       // held for life so voices are individual
      scale: 0.93 + h3 * 0.13,
      lean: 0.18 + h * 0.22,          // spine hunch
      tilt: (h2n - 0.5) * 0.6,        // head roll
      drop: (h3 - 0.5) * 0.55,        // dropped shoulder
      phase: h * 6.28,
      voiceT: 3 + h * 12,
      footT: 0,
      flash: 0,
      diePlayed: false,
      lastEv: 0,
    };
  }

  _applyRows(rows) {
    const seen = new Set();
    for (const r of rows) {
      const id = r[0];
      seen.add(id);
      let v = this.views.get(id);
      if (!v) {
        v = this._makeView(id, r[1], r[2], r[3]);
        v.x = r[1]; v.z = r[2];
        this.views.set(id, v);
      }
      v.netX = r[1]; v.netZ = r[2]; v.netYaw = r[3];
      v.flags = r[5] || 0;
      v.crawler = !!(v.flags & 1);
      const st = r[4];
      if (st !== v.state) {
        if (st === ST.dead && !v.dead) this._onDeath(v);
        v.state = st;
      }
      const ev = r[6] || 0;
      if (ev) this._event(v, ev);
    }
    for (const [id, v] of this.views) {
      if (seen.has(id)) continue;
      // a corpse that streamed out (or rotted away) takes its pockets with it
      if (v.dead) this.o.loot?.dropCorpse?.(id);
      this._release(v);
      this.views.delete(id);
    }
  }

  _event(v, ev) {
    switch (ev) {
      case EV.swing:
        v.pendingClip = v.atkClip;
        this._voice(v, 'attack', 1.05);
        break;
      case EV.scream:
        v.pendingClip = 'z-scream';
        this._voice(v, 'alert', 0.95);
        break;
      case EV.stagger:
        v.pendingClip = 'z-stagger';
        this._voice(v, 'hit', 1.1);
        break;
      case EV.hurt:
        v.pendingClip = 'z-flinch';
        this._voice(v, 'hit', 1.1);
        break;
      case EV.death:
        this._voice(v, 'death');
        break;
      default: break;
    }
  }

  // One place where a body becomes a body — reached both from a snapshot row
  // flipping to `dead` and from the local kill verdict inside damage().
  _onDeath(v) {
    if (v.dead) return;
    v.dead = true;
    this._voice(v, 'death');
    // the corpse is now a searchable container; loot owns the pockets
    // (Z132–Z137) and infers the tag from where it fell
    this.o.loot?.registerCorpse?.({ id: v.id, x: v.netX ?? v.x, z: v.netZ ?? v.z });
  }

  _release(v) {
    if (v.rig) {
      v.rig.root.setEnabled(false);
      v.rig.animator.cancelShots();
      v.rig.view = null;
      v.rig = null;
      v.diePlayed = false;
    }
  }

  // ------------------------------------------------------------------ audio
  // Horde limiter (Z88): 3 concurrent voices, 250 ms between one-shots, and
  // the alert scream always wins arbitration. Without this a 12-infected chase
  // is a wall of mud.
  _voice(v, kind, rateMul = 1) {
    const { sfx } = this.o;
    const spec = VOICE[kind];
    if (!sfx || !spec) return;
    const [role, earshot, vol] = spec;
    const p = this.o.player?.root?.position;
    if (!p) return;
    const d = Math.hypot(v.x - p.x, v.z - p.z);
    if (d > earshot) return;
    const now = performance.now();
    const priority = kind === 'alert' || kind === 'death';
    this._voices = this._voices.filter((t) => now - t < 900);
    if (!priority) {
      if (now - this._voiceT < 250) return;
      if (this._voices.length >= 3) return;
    }
    this._voiceT = now;
    this._voices.push(now);
    const pan = Math.max(-0.8, Math.min(0.8,
      Math.sin(Math.atan2(v.x - p.x, v.z - p.z) - this._camYaw)));
    sfx.play(role, {
      vol: vol * Math.max(0.08, 1 - d / earshot),
      rate: v.pitch * rateMul, jitter: 0.12,
      slice: kind === 'death' ? 0 : kind === 'alert' ? 2.2 : 1.1,
      pan,
    });
  }

  // ----------------------------------------------------------------- update
  update(dt, camYaw = 0) {
    if (!this._ready) return;
    this._camYaw = camYaw;
    if (!this.serverOwned && this.sim) {
      // fixed 5 Hz steps — nothing may depend on frame rate (Z45)
      this._acc += dt;
      let steps = 0;
      while (this._acc >= INF.tick && steps < 3) {
        this._acc -= INF.tick;
        this.sim.tick(INF.tick);
        steps++;
      }
      if (steps) {
        const p = this.o.player.root.position;
        this.rows = this.sim.snapshot(p.x, p.z);
        this._applyRows(this.rows);
      }
    }
    if (!this.views.size) {
      if (this.proxy) this.proxy.thinInstanceCount = 0;
      return;
    }

    const pp = this.o.player.root.position;
    const k = Math.min(1, dt * 6);
    const list = [];
    for (const v of this.views.values()) {
      // glide toward the latest snapshot; teleport-snap on a big delta (Z74)
      if (Math.hypot(v.netX - v.x, v.netZ - v.z) > 30) { v.x = v.netX; v.z = v.netZ; v.yaw = v.netYaw; }
      else {
        v.x += (v.netX - v.x) * k;
        v.z += (v.netZ - v.z) * k;
        v.yaw += angWrap(v.netYaw - v.yaw) * k;
      }
      v.y = this.o.groundY ? this.o.groundY(v.x, v.z) : this.o.terrain.getHeight(v.x, v.z);
      v.d = Math.hypot(v.x - pp.x, v.z - pp.z);
      if (v.flash > 0) v.flash = Math.max(0, v.flash - dt * 4);
      list.push(v);
    }
    // corpses give up their rig to anything still walking
    list.sort((a, b) => (a.d + (a.state === ST.dead ? 45 : 0)) - (b.d + (b.state === ST.dead ? 45 : 0)));

    // first infected inside the mid ring: start pulling the clip set in now so
    // it is retargeted before anything reaches the near tier
    if (!this._clipsP && list.length && list[0].d < 150) this._clipsP = this._loadClips();

    // ---- near tier: pooled skinned rigs ----
    const want = [];
    for (const v of list) {
      if (want.length >= NEAR_MAX) break;
      if (v.d > (v.rig ? NEAR_KEEP : NEAR_R)) break;
      want.push(v);
    }
    for (const v of this.views.values()) if (v.rig && !want.includes(v)) this._release(v);
    for (const v of want) {
      if (v.rig) continue;
      const free = this.pool.find((r) => !r.view);
      if (!free) { this._makeRig(); break; }
      v.rig = free;
      free.view = v;
      free.root.setEnabled(true);
      v.diePlayed = false;
      free.animator.revive();
      free.animator.setBase(v.idleClip, v.rate);
      // snap the layer weights instead of crossfading up from 0.001: a rig
      // entering the near tier must never be visible in its bind pose, and a
      // 0.14 s T-pose flash at 55 m is exactly what that looks like
      free.animator.update(1);
      v.pendingClip = null; // a stale telegraph from before it had a body
      if (!this._warmed) {
        this._warmed = true;
        this.o.sfx?.warm?.('creature.zombie.alert', 'creature.zombie.chase',
          'creature.zombie.attack', 'creature.zombie.moan', 'creature.zombie.hit');
      }
    }
    for (const v of want) if (v.rig) this._driveRig(v, dt);

    // ---- mid tier: thin-instanced shamblers ----
    let n = 0;
    for (const v of list) {
      if (n >= MID_MAX) break;
      if (v.rig) continue;
      if (v.d > MID_R) break;
      const dead = v.state === ST.dead;
      const s = v.scale;
      this._tmpS.set(s * 0.94, s, s * 0.94);
      Quaternion.RotationYawPitchRollToRef(
        v.yaw, 0,
        dead ? Math.PI / 2 * 0.92 : Math.sin(performance.now() / 420 + v.phase) * 0.07,
        this._tmpQ);
      this._tmpP.set(v.x, v.y + (dead ? 0.12 : 0), v.z);
      Matrix.ComposeToRef(this._tmpS, this._tmpQ, this._tmpP, this._tmpM);
      this._tmpM.copyToArray(this._mtx, n * 16);
      n++;
    }
    this.proxy.thinInstanceCount = n;
    if (n) this.proxy.thinInstanceBufferUpdated('matrix');

    // ---- hitboxes for the gun hitscan ----
    this._updateHitboxes(list);

    // ---- ambient voices ----
    for (const v of list) {
      if (v.state === ST.dead || v.d > 60) continue;
      v.voiceT -= dt;
      if (v.voiceT > 0) continue;
      const st = v.state;
      v.voiceT = st === ST.chase || st === ST.attack ? 2 + Math.random() * 2
        : st === ST.alerted ? 4 + Math.random() * 6
          : st === ST.wander ? 8 + Math.random() * 16 : 10 + Math.random() * 22;
      this._voice(v, st === ST.chase || st === ST.attack ? 'chase'
        : st === ST.wander || st === ST.alerted ? 'moan' : 'idle');
    }
  }

  // one rig: ground snap, lurch, clip selection, uneven tempo
  _driveRig(v, dt) {
    const rig = v.rig;
    const a = rig.animator;
    rig.root.position.set(v.x, v.y, v.z);
    const s = v.scale;
    // gaunt: narrower than tall, and no two of them the same height
    rig.root.scaling.set(s * 0.92, s, s * 0.92);
    if (v.flash > 0 || v.flashOn) { this._flashRig(rig, v.flash); v.flashOn = v.flash > 0; }
    const moving = v.state === ST.wander || v.state === ST.alerted
      || v.state === ST.chase || v.state === ST.crawl;
    // rolling lurch — the body never travels level
    const t = performance.now() / 1000;
    const roll = moving ? Math.sin(t * (v.state === ST.chase ? 5.5 : 2.1) + v.phase) * (v.state === ST.chase ? 0.11 : 0.07) : 0;
    Quaternion.RotationYawPitchRollToRef(v.yaw, 0, roll, rig.root.rotationQuaternion);

    if (v.state === ST.dead) {
      if (!v.diePlayed) { v.diePlayed = true; a.die(v.deathClip); }
      a.update(dt);
      // corpses settle 0.12 m into the ground over 0.4 s (Z81)
      v.sink = Math.min(0.12, (v.sink || 0) + dt * 0.3);
      rig.root.position.y = v.y - v.sink;
      return;
    }
    if (v.diePlayed) { v.diePlayed = false; a.revive(); }

    if (v.pendingClip) {
      const clip = v.pendingClip;
      v.pendingClip = null;
      const dur = a.dur(clip);
      // claw swings are retimed onto the 0.45 s windup so the telegraph reads
      const rate = clip === v.atkClip ? Math.max(0.9, dur / 0.75)
        : clip === 'z-scream' ? Math.max(1, dur / 1.4)
          : Math.max(1, dur / 0.7);
      a.playFull(clip, rate * v.rate);
    }

    // uneven speed: the crowd must never march in lockstep
    const wobble = 1 + Math.sin(t * 1.7 + v.phase) * 0.14;
    if (v.state === ST.chase) a.setBase(v.crawler ? 'z-crawl' : v.runClip, v.rate * wobble);
    else if (v.state === ST.crawl) a.setBase('z-crawl', v.rate * wobble);
    else if (v.state === ST.wander || v.state === ST.alerted) {
      a.setBase(v.crawler ? 'z-crawl' : v.walkClip, v.rate * wobble * (v.state === ST.alerted ? 1.5 : 1));
    } else if (v.state === ST.attack) a.setBase(v.idleClip, v.rate);
    else a.setBase(v.idleClip, v.rate * 0.9);
    a.update(dt);

    // footfall drag while running (Z90 — deliberately not surface-aware)
    if (v.state === ST.chase && v.d < 20) {
      v.footT -= dt;
      if (v.footT <= 0) { v.footT = 0.34; this._voice(v, 'footfall'); }
    }
  }

  // Broken pose, applied AFTER the animator has written the bones. This is
  // what stops them reading as the player rig in a different colour: hunched
  // spine, head cocked and thrust forward, one shoulder dropped, one arm
  // hanging dead. Cheap — a handful of quaternion multiplies per visible rig.
  _poseOverlay() {
    if (!this.pool.length) return;
    const t = performance.now() / 1000;
    for (const rig of this.pool) {
      const v = rig.view;
      if (!v || !rig.root.isEnabled() || v.state === ST.dead) continue;
      const b = rig.bones;
      const chase = v.state === ST.chase || v.state === ST.attack;
      const lean = v.lean * (chase ? 1.6 : 1) + (chase ? 0.1 : 0);
      const twitch = Math.sin(t * 3.1 + v.phase) * 0.03;
      this._bend(b.spine, lean * 0.9, 0, v.tilt * 0.25);
      this._bend(b.spine1, lean * 0.55, twitch, v.tilt * 0.2);
      this._bend(b.spine2, lean * 0.35, 0, 0);
      // head fights the hunch and stares forward — the classic infected read
      this._bend(b.neck, -lean * 0.8, v.tilt * 0.6, v.tilt);
      this._bend(b.headB, -lean * 0.5 + twitch, 0, v.tilt * 0.7);
      this._bend(b.lShoulder, 0, 0, v.drop);
      this._bend(b.rShoulder, 0, 0, -v.drop * 0.6);
      if (!chase) {
        // dangling arms while shambling; in a sprint the clip owns them
        this._bend(b.lArm, v.drop > 0 ? 0.34 : 0.06, 0, 0);
        this._bend(b.rArm, v.drop > 0 ? 0.06 : 0.34, 0, 0);
      }
    }
  }

  // Additive bone offset. A clip does not necessarily key every bone, and a
  // bare multiply on an unkeyed bone would compound every frame until the arm
  // span the character. So: if the quaternion is still exactly what we wrote
  // last frame, the animator did not touch it — restore the clean value first.
  _bend(node, pitch, yaw, roll) {
    if (!node) return;
    if (!node.rotationQuaternion) {
      node.rotationQuaternion = Quaternion.FromEulerVector(node.rotation);
    }
    this._poseCache ||= new Map();
    let c = this._poseCache.get(node);
    const cur = node.rotationQuaternion;
    if (c && Math.abs(cur.x - c.ax) < 1e-6 && Math.abs(cur.y - c.ay) < 1e-6
      && Math.abs(cur.z - c.az) < 1e-6 && Math.abs(cur.w - c.aw) < 1e-6) {
      cur.set(c.bx, c.by, c.bz, c.bw);
    }
    if (!c) { c = {}; this._poseCache.set(node, c); }
    c.bx = cur.x; c.by = cur.y; c.bz = cur.z; c.bw = cur.w;
    cur.multiplyInPlace(Quaternion.RotationYawPitchRoll(yaw, pitch, roll));
    c.ax = cur.x; c.ay = cur.y; c.az = cur.z; c.aw = cur.w;
  }

  // Pickable capsules for the gun hitscan. controller.js's ray predicate only
  // accepts meshes named ground/crate/dummy*, and its damage switch routes
  // metadata.dummy into Dummies.damage — so the hitboxes are named `dummy…`
  // and carry an infected proxy that dummies.js hands straight back to us
  // (see Dummies.damage). That keeps src/player/* untouched.
  _hitbox(i) {
    this.boxes ||= [];
    if (this.boxes[i]) return this.boxes[i];
    const head = MeshBuilder.CreateSphere('dummyInfHead' + i, { diameter: 0.42, segments: 8 }, this.scene);
    // body and legs TILE the entity instead of overlapping, so the
    // nearest-hit pick cannot report a knee shot as a torso shot:
    //   legs 0.10 - 0.90 m   (capsule h 0.80 r 0.30, centre 0.50)
    //   body 0.90 - 1.75 m   (capsule h 0.85 r 0.34, centre 1.325)
    // The body capsule used to be h 1.5 at centre 1.0, i.e. 0.25 - 1.75: it
    // owned the legs. Shrinking it is what gives INF_ZONE_Y.legsTop a producer.
    const Z = INF_ZONE_Y;
    const body = MeshBuilder.CreateCapsule('dummyInfBody' + i,
      { height: Z.crown - Z.legsTop, radius: 0.34 }, this.scene);
    const legs = MeshBuilder.CreateCapsule('dummyInfLegs' + i,
      { height: Z.legsTop - Z.feet, radius: 0.3 }, this.scene);
    for (const m of [head, body, legs]) {
      // visibility 0 keeps the mesh pickable while drawing nothing — isVisible
      // false would take it out of the pick entirely
      m.visibility = 0;
      m.isPickable = true;
      m.receiveShadows = false;
      m.setEnabled(false);
    }
    const hb = { head, body, legs, view: null };
    this.boxes[i] = hb;
    return hb;
  }

  _updateHitboxes(list) {
    let n = 0;
    for (const v of list) {
      if (n >= HITBOX_MAX) break;
      if (v.state === ST.dead || v.d > HITBOX_R) continue;
      const hb = this._hitbox(n);
      hb.view = v;
      // The proxy carries its OWN hitbox and the zone that capsule stands for.
      // `hb` is what lets gunHit refuse a proxy captured off a capsule that
      // has since been retired or handed to another entity (see the
      // ghost-hitbox note on the retire loop below); `zone` is what finally
      // gives infected-sim's 'legs' branch a producer.
      const proxy = { infected: v.id, owner: this, hb };
      hb.body.metadata = { dummy: { ...proxy, zone: 'body' }, headshot: false };
      hb.head.metadata = { dummy: { ...proxy, zone: 'head', head: true }, headshot: true };
      hb.body.setEnabled(true);
      hb.head.setEnabled(true);
      const bodyC = (INF_ZONE_Y.crown + INF_ZONE_Y.legsTop) / 2;   // 1.325
      hb.body.position.set(v.x, v.y + (v.crawler ? 0.4 : bodyC), v.z);
      // 0.8, not the old 0.45: the crawler's hit surface must not shrink just
      // because the standing capsule did. 1.5 m x 0.45 = 0.675 m of capsule
      // before; 0.85 m x 0.8 = 0.68 m now, at the same 0.4 m centre.
      hb.body.scaling.set(1, v.crawler ? 0.8 : 1, 1);
      // A crawler has already lost its legs: e.legs passed INF.legCripple, the
      // sim is in ST.crawl and the whole body lies under 0.75 m. A leg capsule
      // there would swallow every shot and keep re-applying limbMul to a
      // target that cannot be crippled twice, so park it — and clear its
      // metadata, or parking it is exactly the ghost the retire loop fixes.
      if (v.crawler) {
        hb.legs.metadata = null;
        hb.legs.setEnabled(false);
      } else {
        hb.legs.metadata = { dummy: { ...proxy, zone: 'legs' }, headshot: false };
        hb.legs.position.set(v.x, v.y + (INF_ZONE_Y.legsTop + INF_ZONE_Y.feet) / 2, v.z);
        hb.legs.setEnabled(true);
      }
      // a rig's skull may be anywhere the broken pose put it — track the bone
      const bone = v.rig?.bones.headB;
      if (bone) {
        const hp = bone.getAbsolutePosition();
        hb.head.position.set(hp.x, hp.y + 0.06, hp.z);
      } else {
        hb.head.position.set(v.x, v.y + (v.crawler ? 0.55 : 1.62), v.z);
      }
      n++;
    }
    // GHOST HITBOXES. setEnabled(false) is NOT enough to retire a hitbox.
    // Babylon's pick walks scene.meshes and, whenever a predicate is supplied,
    // uses it INSTEAD of the enabled/visible/pickable test — see
    // node_modules/@babylonjs/core/Culling/ray.core.js:696-702,
    //   if (predicate) { if (!predicate(mesh, -1)) continue; }
    //   else if (!mesh.isEnabled() || !mesh.isVisible || !mesh.isPickable) continue;
    // and controller.js's hitscan has always supplied one. So a parked capsule
    // stayed fully hittable at the last position it was parked at, still
    // carrying the metadata of whatever entity it last stood for: a shot into
    // empty air damaged an infected somewhere else, or one that had already
    // died. Clearing the metadata is what actually retires it — the pick may
    // still report the mesh, but there is no longer an infected behind it.
    for (let i = n; i < (this.boxes?.length || 0); i++) {
      const hb = this.boxes[i];
      hb.view = null;
      for (const m of [hb.body, hb.head, hb.legs]) {
        m.metadata = null;
        m.setEnabled(false);
      }
    }
  }

  // ---------------------------------------------------------------- combat
  // Same signature and return value as Dummies.meleeHit / Animals.meleeHit —
  // main.js chains it into world.remoteHit so src/player/* needs no change.
  // This was the reach + 0.5 / dot 0.5 copy — the widest of the four, and the
  // one with the most to lose from having no occlusion test: an infected
  // standing on the far side of a shut door was meleeable through it, and one
  // on the floor above was meleeable through the ceiling. Both are now the
  // shared resolver's problem (src/world/melee-arc.mjs). The zone stays
  // 'body': a swing carries no impact height, so inventing one here would be
  // the opposite of giving 'legs' an honest producer.
  meleeHit(playerPos, heading, reach, dmg) {
    let hits = 0;
    const swing = meleeSwing(this.o.world, playerPos, heading, reach);
    for (const v of this.views.values()) {
      if (v.state === ST.dead) continue;
      if (!swing.test(v.x, v.y, v.z, v)) continue;
      this.damage(v, dmg, 'body', 'melee', playerPos);
      hits++;
    }
    return hits;
  }

  // The gun path, for BOTH answers: the mesh pick (controller.js ->
  // Dummies.damage -> here, because our hitboxes are named `dummy…` and carry
  // an { infected, owner } proxy) and the query ray (main.js's bodiesNear
  // source puts the SAME proxy shape in hit.entity, and controller.js
  // _damageTarget calls tag.owner.gunHit directly).
  //
  // `zone` is the query's 'head' | 'body' | 'legs'. It is the fifth argument
  // so the mesh pick, which has no hit height, can keep passing four.
  gunHit(proxy, dmg, fromDir, headshot, zone) {
    // Liveness, the second lock on the ghost hitboxes described in
    // _updateHitboxes: honour this proxy only while the capsule it came off is
    // still the live hitbox for that same entity. A proxy read from a capsule
    // that was retired, or re-assigned to a different infected, is dropped.
    const hb = proxy.hb;
    if (hb && (hb.view == null || hb.view.id !== proxy.infected || !hb.body.isEnabled())) return;
    const v = this.views.get(proxy.infected);
    if (!v) return;
    const p = this.o.player?.root?.position;
    // Zone precedence: an explicit zone from the query ray, else the capsule
    // the pick actually hit (body / head / legs — the capsules are cut at the
    // same INF_ZONE_Y heights the query's body records use, so the two agree),
    // else the pre-zone headshot flag so nothing regresses if an older code
    // path hands over a proxy with neither.
    const z = zone === 'head' || zone === 'body' || zone === 'legs'
      ? zone : (proxy.zone || (headshot ? 'head' : 'body'));
    this.damage(v, dmg, z, 'gun', p);
  }

  damage(v, dmg, zone, src, fromPos) {
    if (v.state === ST.dead) return 0;
    // blood spray / impact decal height, per zone — 'legs' is new and would
    // otherwise spray out of the chest for a shot in the shin
    const y = v.y + (zone === 'head' ? 1.62 : zone === 'legs' ? 0.5 : 1.1);
    const dirX = fromPos ? (v.x - fromPos.x) : 0;
    const dirZ = fromPos ? (v.z - fromPos.z) : 1;
    const len = Math.hypot(dirX, dirZ) || 1;
    this._spray(v.x, y, v.z, dirX / len, dirZ / len);
    this.o.effects?.impact?.(new Vector3(v.x, y, v.z), new Vector3(-dirX / len, 0.4, -dirZ / len), 'soil');
    v.flash = 1;
    let applied = dmg;
    if (this.serverOwned) {
      // server owns the verdict; it comes back through the snapshot
      this.onNet?.({ k: 'inf-dmg', id: v.id, dmg, zone, src });
    } else {
      applied = this.sim.applyDamage(v.id, dmg, zone, src,
        fromPos ? fromPos.x : null, fromPos ? fromPos.z : null);
      // reflect the verdict this frame instead of waiting for the next tick
      const rows = this.sim.snapshot(v.x, v.z, 4);
      for (const r of rows) if (r[0] === v.id) { v.state = r[4]; if (r[6]) this._event(v, r[6]); }
      if (v.state === ST.dead) this._onDeath(v);
    }
    this._floater(v, Math.round(applied), zone === 'head');
    this._voice(v, 'hit', 1.05);
    return applied;
  }

  // damage numbers, same treatment dummies.js gives them
  _floater(v, dmg, headshot) {
    const { engine, camera, scene } = { ...this.o, scene: this.scene };
    if (!engine || !camera?.cam || typeof document === 'undefined') return;
    const w = engine.getRenderWidth(), h = engine.getRenderHeight();
    const sp = Vector3.Project(new Vector3(v.x, v.y + 1.8, v.z), Matrix.Identity(),
      scene.getTransformMatrix(), camera.cam.viewport.toGlobal(w, h));
    if (sp.z > 1) return;
    const el = document.createElement('div');
    el.textContent = headshot ? `${dmg} HS!` : `${dmg}`;
    el.style.cssText = `position:fixed;left:${sp.x}px;top:${sp.y}px;color:${headshot ? '#ff5a3c' : '#e8d9a0'};`
      + 'font:bold 16px monospace;pointer-events:none;z-index:20;transition:all .7s ease-out;text-shadow:0 1px 2px #000;';
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.top = (sp.y - 60) + 'px'; el.style.opacity = 0; });
    setTimeout(() => el.remove(), 750);
  }

  // a ping RELAYED from another player (main.js `case 'noise'`). It is already
  // sanitized — by the server, or by our own emitter before it went out — so it
  // only has to reach the local sim, and never bounces back onto the wire. In
  // mirror mode the server's sim heard it first and this is a no-op (Z4).
  applyPing(ev) {
    if (!ev || this.serverOwned) return;
    const p = sanitizePing(ev);   // a relay is still somebody else's claim
    if (p) this.sim?.ping(p.x, p.z, p.r);
  }

  // a loud one-shot: the acting client pings the sim (Z27–Z28)
  ping(x, z, r) {
    if (this.serverOwned) { this.onNet?.({ k: 'noise', x: +x.toFixed(1), z: +z.toFixed(1), r }); return; }
    this.sim?.ping(x, z, r);
  }

  // ------------------------------------------------------------- test hook
  debug() {
    const byState = [0, 0, 0, 0, 0, 0, 0, 0];
    let rigs = 0;
    for (const v of this.views.values()) {
      byState[v.state] = (byState[v.state] || 0) + 1;
      if (v.rig) rigs++;
    }
    return {
      active: this.views.size,
      planned: this.sim?.planned ?? 0,
      simActive: this.sim?.active.size ?? 0,
      rigs, pool: this.pool.length,
      proxies: this.proxy?.thinInstanceCount ?? 0,
      clips: this.master ? Object.keys(this.master).length : 0,
      serverOwned: this.serverOwned,
      byState,
      states: Object.fromEntries(ST_NAMES.map((n, i) => [n, byState[i] || 0])),
      near: [...this.views.values()]
        .filter((v) => v.rig)
        .map((v) => ({ id: v.id, d: +(v.d || 0).toFixed(1), state: ST_NAMES[v.state] })),
    };
  }
}
